import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { 
  Calculator, DollarSign, PieChart, Calendar, FileText, 
  TrendingUp, TrendingDown, Download, ChevronDown, Info,
  Building, Percent, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

// 2024 Federal Tax Brackets (Single filer - most common for self-employed)
const FEDERAL_TAX_BRACKETS_SINGLE = [
  { min: 0, max: 11600, rate: 0.10 },
  { min: 11600, max: 47150, rate: 0.12 },
  { min: 47150, max: 100525, rate: 0.22 },
  { min: 100525, max: 191950, rate: 0.24 },
  { min: 191950, max: 243725, rate: 0.32 },
  { min: 243725, max: 609350, rate: 0.35 },
  { min: 609350, max: Infinity, rate: 0.37 }
];

// Self-Employment Tax Rate
const SE_TAX_RATE = 0.153; // 15.3% (12.4% Social Security + 2.9% Medicare)
const SE_TAX_DEDUCTION = 0.5; // Can deduct 50% of SE tax

// State Tax Rates (simplified - actual rates vary)
const STATE_TAX_RATES = {
  'none': { name: 'No State Tax', rate: 0 },
  'AL': { name: 'Alabama', rate: 0.05 },
  'AK': { name: 'Alaska', rate: 0 },
  'AZ': { name: 'Arizona', rate: 0.025 },
  'AR': { name: 'Arkansas', rate: 0.049 },
  'CA': { name: 'California', rate: 0.093 },
  'CO': { name: 'Colorado', rate: 0.044 },
  'CT': { name: 'Connecticut', rate: 0.0699 },
  'DE': { name: 'Delaware', rate: 0.066 },
  'FL': { name: 'Florida', rate: 0 },
  'GA': { name: 'Georgia', rate: 0.0549 },
  'HI': { name: 'Hawaii', rate: 0.11 },
  'ID': { name: 'Idaho', rate: 0.058 },
  'IL': { name: 'Illinois', rate: 0.0495 },
  'IN': { name: 'Indiana', rate: 0.0315 },
  'IA': { name: 'Iowa', rate: 0.06 },
  'KS': { name: 'Kansas', rate: 0.057 },
  'KY': { name: 'Kentucky', rate: 0.045 },
  'LA': { name: 'Louisiana', rate: 0.0425 },
  'ME': { name: 'Maine', rate: 0.0715 },
  'MD': { name: 'Maryland', rate: 0.0575 },
  'MA': { name: 'Massachusetts', rate: 0.05 },
  'MI': { name: 'Michigan', rate: 0.0425 },
  'MN': { name: 'Minnesota', rate: 0.0985 },
  'MS': { name: 'Mississippi', rate: 0.05 },
  'MO': { name: 'Missouri', rate: 0.0495 },
  'MT': { name: 'Montana', rate: 0.0675 },
  'NE': { name: 'Nebraska', rate: 0.0684 },
  'NV': { name: 'Nevada', rate: 0 },
  'NH': { name: 'New Hampshire', rate: 0 },
  'NJ': { name: 'New Jersey', rate: 0.1075 },
  'NM': { name: 'New Mexico', rate: 0.059 },
  'NY': { name: 'New York', rate: 0.109 },
  'NC': { name: 'North Carolina', rate: 0.0525 },
  'ND': { name: 'North Dakota', rate: 0.029 },
  'OH': { name: 'Ohio', rate: 0.04 },
  'OK': { name: 'Oklahoma', rate: 0.0475 },
  'OR': { name: 'Oregon', rate: 0.099 },
  'PA': { name: 'Pennsylvania', rate: 0.0307 },
  'RI': { name: 'Rhode Island', rate: 0.0599 },
  'SC': { name: 'South Carolina', rate: 0.07 },
  'SD': { name: 'South Dakota', rate: 0 },
  'TN': { name: 'Tennessee', rate: 0 },
  'TX': { name: 'Texas', rate: 0 },
  'UT': { name: 'Utah', rate: 0.0485 },
  'VT': { name: 'Vermont', rate: 0.0875 },
  'VA': { name: 'Virginia', rate: 0.0575 },
  'WA': { name: 'Washington', rate: 0 },
  'WV': { name: 'West Virginia', rate: 0.065 },
  'WI': { name: 'Wisconsin', rate: 0.0765 },
  'WY': { name: 'Wyoming', rate: 0 },
  'DC': { name: 'Washington D.C.', rate: 0.1075 }
};

// Standard Deduction 2024
const STANDARD_DEDUCTION_SINGLE = 14600;

const TaxCalculator = () => {
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [selectedState, setSelectedState] = useState('none');
  const [filingStatus, setFilingStatus] = useState('single');
  const [additionalIncome, setAdditionalIncome] = useState(0);
  const [additionalDeductions, setAdditionalDeductions] = useState(0);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchFinancialData();
  }, [taxYear]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Get all receipts (expenses/deductions)
      const receiptsRes = await api.get('/receipts');
      const incomeRes = await api.get('/income');
      
      // Filter by tax year
      const yearReceipts = receiptsRes.data.filter(r => {
        const date = r.date || '';
        const year = date.includes('/') ? date.split('/')[2] : date.split('-')[0];
        return parseInt(year) === taxYear;
      });
      
      const yearIncome = incomeRes.data.filter(i => {
        const date = i.date || '';
        const year = date.includes('/') ? date.split('/')[2] : date.split('-')[0];
        return parseInt(year) === taxYear;
      });

      // Calculate totals
      const totalIncome = yearIncome.reduce((sum, i) => sum + (i.amount || 0), 0);
      const totalExpenses = yearReceipts.reduce((sum, r) => sum + (r.amount || 0), 0);
      const businessExpenses = yearReceipts.filter(r => r.business_expense).reduce((sum, r) => sum + (r.amount || 0), 0);

      // Group expenses by category
      const expensesByCategory = {};
      yearReceipts.forEach(r => {
        const cat = r.category || 'Other';
        expensesByCategory[cat] = (expensesByCategory[cat] || 0) + (r.amount || 0);
      });

      setFinancialData({
        totalIncome,
        totalExpenses,
        businessExpenses,
        expensesByCategory,
        receiptsCount: yearReceipts.length,
        incomeCount: yearIncome.length
      });
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate Federal Income Tax
  const calculateFederalTax = (taxableIncome) => {
    let tax = 0;
    let remainingIncome = taxableIncome;
    const brackets = FEDERAL_TAX_BRACKETS_SINGLE;

    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;
      const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
      tax += taxableInBracket * bracket.rate;
      remainingIncome -= taxableInBracket;
    }

    return tax;
  };

  // Calculate all taxes
  const calculateTaxes = () => {
    if (!financialData) return null;

    const grossIncome = financialData.totalIncome + parseFloat(additionalIncome || 0);
    const businessDeductions = financialData.businessExpenses + parseFloat(additionalDeductions || 0);
    
    // Net self-employment income
    const netSEIncome = grossIncome - businessDeductions;
    
    // Self-Employment Tax (on 92.35% of net SE income)
    const seTaxableIncome = netSEIncome * 0.9235;
    const selfEmploymentTax = Math.max(0, seTaxableIncome * SE_TAX_RATE);
    
    // Deduction for 1/2 of SE tax
    const seDeduction = selfEmploymentTax * SE_TAX_DEDUCTION;
    
    // Adjusted Gross Income
    const agi = netSEIncome - seDeduction;
    
    // Taxable Income (after standard deduction)
    const taxableIncome = Math.max(0, agi - STANDARD_DEDUCTION_SINGLE);
    
    // Federal Income Tax
    const federalTax = calculateFederalTax(taxableIncome);
    
    // State Tax
    const stateRate = STATE_TAX_RATES[selectedState]?.rate || 0;
    const stateTax = taxableIncome * stateRate;
    
    // Total Tax
    const totalTax = federalTax + selfEmploymentTax + stateTax;
    
    // Effective Tax Rate
    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;
    
    // Quarterly Estimated Payments
    const quarterlyPayment = totalTax / 4;

    return {
      grossIncome,
      businessDeductions,
      netSEIncome,
      selfEmploymentTax,
      seDeduction,
      agi,
      standardDeduction: STANDARD_DEDUCTION_SINGLE,
      taxableIncome,
      federalTax,
      stateTax,
      stateRate: stateRate * 100,
      totalTax,
      effectiveRate,
      quarterlyPayment,
      takeHome: grossIncome - totalTax
    };
  };

  const taxes = calculateTaxes();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleExportPDF = () => {
    // Create printable content
    const printContent = `
      TAX SUMMARY REPORT - ${taxYear}
      ================================
      
      INCOME
      Gross Business Income: ${formatCurrency(taxes?.grossIncome)}
      
      DEDUCTIONS
      Business Expenses: ${formatCurrency(taxes?.businessDeductions)}
      SE Tax Deduction: ${formatCurrency(taxes?.seDeduction)}
      Standard Deduction: ${formatCurrency(taxes?.standardDeduction)}
      
      TAX CALCULATIONS
      Net SE Income: ${formatCurrency(taxes?.netSEIncome)}
      Adjusted Gross Income: ${formatCurrency(taxes?.agi)}
      Taxable Income: ${formatCurrency(taxes?.taxableIncome)}
      
      TAXES OWED
      Federal Income Tax: ${formatCurrency(taxes?.federalTax)}
      Self-Employment Tax: ${formatCurrency(taxes?.selfEmploymentTax)}
      State Tax (${selectedState}): ${formatCurrency(taxes?.stateTax)}
      
      TOTAL TAX: ${formatCurrency(taxes?.totalTax)}
      Effective Tax Rate: ${taxes?.effectiveRate.toFixed(1)}%
      
      QUARTERLY ESTIMATED PAYMENTS
      Q1 (Apr 15): ${formatCurrency(taxes?.quarterlyPayment)}
      Q2 (Jun 15): ${formatCurrency(taxes?.quarterlyPayment)}
      Q3 (Sep 15): ${formatCurrency(taxes?.quarterlyPayment)}
      Q4 (Jan 15): ${formatCurrency(taxes?.quarterlyPayment)}
      
      Generated by FinScan USA
    `;
    
    const blob = new Blob([printContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax_summary_${taxYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Tax summary downloaded');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Calculator className="w-8 h-8 mr-3 text-green-400" />
              Tax Calculator
            </h1>
            <p className="text-muted-foreground mt-1">Estimate your self-employment taxes</p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={taxYear}
              onChange={(e) => setTaxYear(parseInt(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              onClick={handleExportPDF}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
            <label className="block text-sm text-muted-foreground mb-2">State</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2"
            >
              {Object.entries(STATE_TAX_RATES).map(([code, info]) => (
                <option key={code} value={code}>
                  {info.name} ({(info.rate * 100).toFixed(1)}%)
                </option>
              ))}
            </select>
          </div>
          
          <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
            <label className="block text-sm text-muted-foreground mb-2">Additional Income</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="number"
                value={additionalIncome}
                onChange={(e) => setAdditionalIncome(e.target.value)}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2"
              />
            </div>
          </div>
          
          <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
            <label className="block text-sm text-muted-foreground mb-2">Additional Deductions</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="number"
                value={additionalDeductions}
                onChange={(e) => setAdditionalDeductions(e.target.value)}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2"
              />
            </div>
          </div>
        </div>

        {/* Main Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-sm text-green-300">Gross Income</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{formatCurrency(taxes?.grossIncome)}</p>
          </div>
          
          <div className="p-5 bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-red-400" />
              <span className="text-sm text-red-300">Total Tax</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{formatCurrency(taxes?.totalTax)}</p>
          </div>
          
          <div className="p-5 bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-blue-300">Effective Rate</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{taxes?.effectiveRate.toFixed(1)}%</p>
          </div>
          
          <div className="p-5 bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-purple-300">Take Home</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{formatCurrency(taxes?.takeHome)}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Tax Breakdown */}
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <PieChart className="w-5 h-5 mr-2 text-indigo-400" />
              Tax Breakdown
            </h3>
            
            <div className="space-y-4">
              {/* Federal Tax */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Federal Income Tax</span>
                  <span className="font-mono">{formatCurrency(taxes?.federalTax)}</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${taxes?.totalTax > 0 ? (taxes.federalTax / taxes.totalTax) * 100 : 0}%` }}
                  />
                </div>
              </div>
              
              {/* SE Tax */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Self-Employment Tax (15.3%)</span>
                  <span className="font-mono">{formatCurrency(taxes?.selfEmploymentTax)}</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full"
                    style={{ width: `${taxes?.totalTax > 0 ? (taxes.selfEmploymentTax / taxes.totalTax) * 100 : 0}%` }}
                  />
                </div>
              </div>
              
              {/* State Tax */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>State Tax ({STATE_TAX_RATES[selectedState]?.name})</span>
                  <span className="font-mono">{formatCurrency(taxes?.stateTax)}</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${taxes?.totalTax > 0 ? (taxes.stateTax / taxes.totalTax) * 100 : 0}%` }}
                  />
                </div>
              </div>
              
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between font-semibold">
                  <span>Total Tax Liability</span>
                  <span className="text-red-400 font-mono">{formatCurrency(taxes?.totalTax)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quarterly Payments */}
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-green-400" />
              Quarterly Estimated Payments
            </h3>
            
            <div className="space-y-3">
              {[
                { q: 'Q1', date: 'April 15', color: 'bg-blue-500' },
                { q: 'Q2', date: 'June 15', color: 'bg-green-500' },
                { q: 'Q3', date: 'September 15', color: 'bg-yellow-500' },
                { q: 'Q4', date: 'January 15 (next year)', color: 'bg-purple-500' }
              ].map((quarter) => (
                <div key={quarter.q} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${quarter.color}`} />
                    <div>
                      <p className="font-medium">{quarter.q}</p>
                      <p className="text-xs text-muted-foreground">{quarter.date}</p>
                    </div>
                  </div>
                  <p className="font-mono font-semibold">{formatCurrency(taxes?.quarterlyPayment)}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-yellow-400 mt-0.5" />
                <p className="text-xs text-yellow-200">
                  Pay estimated taxes quarterly to avoid penalties. Use IRS Form 1040-ES.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Calculations */}
        <div className="mt-6 p-6 bg-white/5 border border-white/10 rounded-xl">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between"
          >
            <h3 className="text-lg font-semibold flex items-center">
              <FileText className="w-5 h-5 mr-2 text-purple-400" />
              Detailed Calculations
            </h3>
            <ChevronDown className={`w-5 h-5 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>
          
          {showDetails && (
            <div className="mt-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Income Section */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium mb-3 text-green-400">Income</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Business Income (from FinScan)</span>
                      <span className="font-mono">{formatCurrency(financialData?.totalIncome)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Additional Income</span>
                      <span className="font-mono">{formatCurrency(additionalIncome)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t border-white/10 pt-2">
                      <span>Gross Income</span>
                      <span className="font-mono">{formatCurrency(taxes?.grossIncome)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Deductions Section */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium mb-3 text-red-400">Deductions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Business Expenses (from FinScan)</span>
                      <span className="font-mono">{formatCurrency(financialData?.businessExpenses)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Additional Deductions</span>
                      <span className="font-mono">{formatCurrency(additionalDeductions)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>1/2 Self-Employment Tax</span>
                      <span className="font-mono">{formatCurrency(taxes?.seDeduction)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Standard Deduction</span>
                      <span className="font-mono">{formatCurrency(taxes?.standardDeduction)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Calculation Steps */}
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="font-medium mb-3 text-blue-400">Calculation Steps</h4>
                <div className="space-y-2 text-sm font-mono">
                  <div className="flex justify-between">
                    <span>1. Gross Income</span>
                    <span>{formatCurrency(taxes?.grossIncome)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>2. − Business Expenses</span>
                    <span>−{formatCurrency(taxes?.businessDeductions)}</span>
                  </div>
                  <div className="flex justify-between text-yellow-400">
                    <span>= Net Self-Employment Income</span>
                    <span>{formatCurrency(taxes?.netSEIncome)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>3. − 1/2 SE Tax Deduction</span>
                    <span>−{formatCurrency(taxes?.seDeduction)}</span>
                  </div>
                  <div className="flex justify-between text-blue-400">
                    <span>= Adjusted Gross Income (AGI)</span>
                    <span>{formatCurrency(taxes?.agi)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>4. − Standard Deduction</span>
                    <span>−{formatCurrency(taxes?.standardDeduction)}</span>
                  </div>
                  <div className="flex justify-between text-green-400 font-semibold">
                    <span>= Taxable Income</span>
                    <span>{formatCurrency(taxes?.taxableIncome)}</span>
                  </div>
                </div>
              </div>

              {/* Business Expenses by Category */}
              {financialData?.expensesByCategory && Object.keys(financialData.expensesByCategory).length > 0 && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium mb-3 text-purple-400">Business Expenses by Category</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(financialData.expensesByCategory)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => (
                        <div key={category} className="flex justify-between">
                          <span>{category}</span>
                          <span className="font-mono">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-200">
              <p className="font-medium mb-1">Disclaimer</p>
              <p>This calculator provides estimates only and should not be considered tax advice. 
              Tax laws are complex and vary by situation. Please consult a qualified tax professional 
              for accurate tax planning and filing. Always verify calculations with official IRS resources.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCalculator;
